import React, { useState, useMemo, useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import ScoreCard from "@/charts/ScoreCard";
import useLoadData from "@/hooks/useLoadData";
import DonutChart from "@/charts/DonutChart";
import TimeseriesChart from "@/charts/TimeseriesChart";
import SingleColumnTable from "@/charts/SingleColumnTable";
import TwoColumnTable from "@/charts/TwoColumnTable";
import CustomDropdown from "@/components/ui/CustomDropdown";
import { DatePicker } from "antd";
import "@/pages/page.css";
import { AE_BACKEND_URL } from "@/config";
import {
  CUSTOM_DATE_RANGE,
  DEFAULT_DATE_RANGE,
  PRESET_DATE_RANGES,
  getPresetDays,
  getPresetValue,
  getQueryTimestamps,
  isCustomRangeDateDisabled,
} from "./analyticsDateRange";

const FAILED_ANS_TRUNCATE_LENGTH = 500;

const { RangePicker } = DatePicker;

const funnelStageColors = {
  top: { bg: "var(--funnel-top-bg)", color: "var(--funnel-top-text)" },
  middle: { bg: "var(--funnel-mid-bg)", color: "var(--funnel-mid-text)" },
  bottom: { bg: "var(--funnel-bottom-bg)", color: "var(--funnel-bottom-text)" },
};

export default function Analytics() {
  const { t } = useTranslation();
  const [dateRangeOption, setDateRangeOption] = useState(DEFAULT_DATE_RANGE);
  const [customDateRange, setCustomDateRange] = useState(null); // [dayjs, dayjs] | null
  const [isDateRangePickerOpen, setIsDateRangePickerOpen] = useState(false);
  const [activeCustomDateRangeInput, setActiveCustomDateRangeInput] =
    useState(null); // "start" | "end" | null
  const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const isCustomDateRangeMode = dateRangeOption === CUSTOM_DATE_RANGE;
  const hasCustomRange = Boolean(
    isCustomDateRangeMode && customDateRange?.[0] && customDateRange?.[1],
  );
  // Only a fully selected custom range is committed to the query. Keying the
  // memo on these date strings (instead of the picker's value object) keeps
  // the request URLs stable while a range is only partially selected, so no
  // extra requests are fired mid-selection.
  const committedCustomStart = hasCustomRange
    ? customDateRange[0].format("YYYY-MM-DD")
    : null;
  const committedCustomEnd = hasCustomRange
    ? customDateRange[1].format("YYYY-MM-DD")
    : null;
  // Last selected preset stays in effect while a custom range is incomplete.
  const [checkPoint, setCheckPoint] = useState(7);

  const { startTimestamp, endTimestamp } = useMemo(
    () =>
      getQueryTimestamps({
        checkPoint,
        customStart: committedCustomStart,
        customEnd: committedCustomEnd,
      }),
    [checkPoint, committedCustomStart, committedCustomEnd],
  );

  // Closing the picker without a complete range would leave the dropdown on
  // "Custom date range" while the page shows preset data, so go back to the
  // preset in effect. This reacts to committed state rather than living in
  // onOpenChange: rc-picker fires onChange and onOpenChange(false) in the same
  // batch, so that callback still sees the range as empty when one is picked.
  useEffect(() => {
    if (isCustomDateRangeMode && !isDateRangePickerOpen && !hasCustomRange) {
      setDateRangeOption(getPresetValue(checkPoint));
    }
  }, [isCustomDateRangeMode, isDateRangePickerOpen, hasCustomRange, checkPoint]);

  const isDateRangeDateDisabled = (current, info) =>
    isCustomRangeDateDisabled(current, {
      from: info?.from,
      activeInput: activeCustomDateRangeInput,
      customDateRange,
    });

  const getFunnelBadge = useCallback((stage) => {
    if (!stage) return null;
    const style = funnelStageColors[stage.toLowerCase()] ?? {
      bg: "var(--action-primary)",
      color: "white",
    };
    const stageLabel =
      stage.toLowerCase() === "top"
        ? t("tracking.topFunnel", "Top")
        : stage.toLowerCase() === "middle"
          ? t("tracking.middleFunnel", "Middle")
          : stage.toLowerCase() === "bottom"
            ? t("tracking.bottomFunnel", "Bottom")
            : stage;
    return (
      <span
        className="badge"
        style={{ backgroundColor: style.bg, color: style.color }}
      >
        {stageLabel}
      </span>
    );
  }, [t]);

  const getBrandedBadge = useCallback((isBranded) => {
    if (isBranded === true)
      return (
        <span
          className="badge"
          style={{
            backgroundColor: "var(--badge-branded-bg)",
            color: "var(--badge-branded-text)",
          }}
        >
          {t("promptLibrary.branded", "Branded")}
        </span>
      );
    if (isBranded === false)
      return (
        <span
          className="badge"
          style={{
            backgroundColor: "var(--badge-unbranded-bg)",
            color: "var(--badge-unbranded-text)",
          }}
        >
          {t("promptLibrary.nonBranded", "Non-Branded")}
        </span>
      );
    return null;
  }, [t]);

  const [analyticsOverview, analyticsOverviewIsLoaded, analyticsOverviewError] =
    useLoadData(
      `${AE_BACKEND_URL}/api/v1/answer-engine-analytics/overview?start_timestamp=${encodeURIComponent(startTimestamp)}&end_timestamp=${encodeURIComponent(endTimestamp)}&timezone=${encodeURIComponent(userTimezone)}`,
    );

  const [
    funnelStageDistribution,
    funnelStageDistributionIsLoaded,
    funnelStageDistributionError,
  ] = useLoadData(
    `${AE_BACKEND_URL}/api/v1/answer-engine-analytics/funnel-stage-distribution?start_timestamp=${encodeURIComponent(startTimestamp)}&end_timestamp=${encodeURIComponent(endTimestamp)}&timezone=${encodeURIComponent(userTimezone)}`,
  );

  const [
    chatSessionCountTimeseries,
    chatSessionCountTimeseriesIsLoaded,
    chatSessionCountTimeseriesError,
  ] = useLoadData(
    `${AE_BACKEND_URL}/api/v1/answer-engine-analytics/chat-session-count-timeseries?start_timestamp=${encodeURIComponent(startTimestamp)}&end_timestamp=${encodeURIComponent(endTimestamp)}&timezone=${encodeURIComponent(userTimezone)}`,
  );

  const [failedAnswers, failedAnswersIsLoaded, failedAnswersError] =
    useLoadData(
      `${AE_BACKEND_URL}/api/v1/answer-engine-analytics/failed-answers?start_timestamp=${encodeURIComponent(startTimestamp)}&end_timestamp=${encodeURIComponent(endTimestamp)}&timezone=${encodeURIComponent(userTimezone)}`,
    );

  const enrichedFailedAnswers = useMemo(
    () =>
      (failedAnswers ?? []).map((item) => ({
        ...item,
        detailed_answer: (
          <div>
            <b>{item.query}</b>
            <br />
            <div className="d-flex gap-1 mt-1">
              {getFunnelBadge(item.funnel_stage)}
              {getBrandedBadge(item.is_branded)}
            </div>
            <br />
            <small className="text-muted">
              Response:{" "}
              {item.answer
                ? item.answer.length > FAILED_ANS_TRUNCATE_LENGTH
                  ? item.answer.slice(0, FAILED_ANS_TRUNCATE_LENGTH) + "..."
                  : item.answer
                : "—"}
            </small>
          </div>
        ),
      })),
    [failedAnswers, getFunnelBadge, getBrandedBadge],
  );

  const [
    highValueQuestions,
    highValueQuestionsIsLoaded,
    highValueQuestionsError,
  ] = useLoadData(
    `${AE_BACKEND_URL}/api/v1/answer-engine-analytics/high-value-questions?start_timestamp=${encodeURIComponent(startTimestamp)}&end_timestamp=${encodeURIComponent(endTimestamp)}&timezone=${encodeURIComponent(userTimezone)}`,
  );

  const enrichedHighValueQuestions = useMemo(
    () =>
      (highValueQuestions ?? []).map((item) => ({
        ...item,
        detailed_question: (
          <div>
            <b>{item.query}</b>
            <br />
            <div className="d-flex gap-1 mt-1">
              {getFunnelBadge(item.funnel_stage)}
              {getBrandedBadge(item.is_branded)}
            </div>
          </div>
        ),
      })),
    [highValueQuestions, getFunnelBadge, getBrandedBadge],
  );

  const [
    queryTopicDistribution,
    queryTopicDistributionIsLoaded,
    queryTopicDistributionError,
  ] = useLoadData(
    `${AE_BACKEND_URL}/api/v1/answer-engine-analytics/query-topic-distribution?start_timestamp=${encodeURIComponent(startTimestamp)}&end_timestamp=${encodeURIComponent(endTimestamp)}&timezone=${encodeURIComponent(userTimezone)}`,
  );

  const handleDateRangeChange = (value) => {
    setDateRangeOption(value);
    if (value === CUSTOM_DATE_RANGE) {
      setIsDateRangePickerOpen(true);
      return;
    }
    setCustomDateRange(null);
    setIsDateRangePickerOpen(false);
    setCheckPoint(getPresetDays(value));
  };

  return (
    <div className="app-container">
      <main className="content bg-page">
        <div className="container-fluid p-4">
          {/* Heading */}
          <h3 className="h4 fw-bold text-dark">
            {t("answerEngine.analyticsHeading", "Answer Engine Analytics")}
          </h3>
          <p className="mt-1 fs-6 text-secondary">
            {t(
              "answerEngine.analyticsPageSubtitle",
              "Monitor your Answer Engine performance and user engagement metrics",
            )}
          </p>

          {/* Filter Area */}
          <div
            className="card card-radius border-1 shadow-sm mb-4"
            style={{ borderColor: "var(--border)" }}
            data-tour="answer-engine.date-range"
          >
            <div className="card-body">
              <div className="row align-items-center">
                <div className="col-12 col-sm-auto mb-3 mb-sm-0">
                  <div className="d-flex align-items-center">
                    <i
                      className="bi bi-funnel text-muted me-2"
                      style={{ fontSize: "16px" }}
                    ></i>
                    <h6 className="mb-0 text-muted fw-medium">
                      {t("dashboard.filterTitle", "Filter Dashboard")}
                    </h6>
                  </div>
                </div>

                <div className="col-12 col-sm">
                  <div className="row g-3 justify-content-end">
                    <div className="col-12 col-md-auto">
                      <div className="d-flex align-items-center flex-wrap">
                        <span className="text-muted me-2 small">
                          {isCustomDateRangeMode
                            ? `${t("dashboard.dateRange", "Date range")}:`
                            : t("dashboard.compareLabel", "Show changes between most recent:")}
                        </span>
                        <CustomDropdown
                          options={[
                            ...PRESET_DATE_RANGES.map((r) => ({
                              value: r.value,
                              label: t(r.i18nKey, r.value),
                            })),
                            {
                              value: CUSTOM_DATE_RANGE,
                              label: t(
                                "dashboard.customDateRange",
                                "Custom date range",
                              ),
                            },
                          ]}
                          value={dateRangeOption}
                          onChange={handleDateRangeChange}
                          style={{ minWidth: "200px" }}
                        />
                        {isCustomDateRangeMode && (
                          <RangePicker
                            className="ms-2"
                            open={isDateRangePickerOpen}
                            onOpenChange={(open) => {
                              setIsDateRangePickerOpen(open);
                              if (!open) setActiveCustomDateRangeInput(null);
                            }}
                            value={customDateRange}
                            onFocus={(_, info) =>
                              setActiveCustomDateRangeInput(info?.range ?? null)
                            }
                            onCalendarChange={(_, __, info) =>
                              setActiveCustomDateRangeInput(info?.range ?? null)
                            }
                            onChange={(dates) => {
                              setCustomDateRange(dates);
                              if (dates?.[0] && dates?.[1]) {
                                setActiveCustomDateRangeInput(null);
                                setIsDateRangePickerOpen(false);
                              }
                            }}
                            disabledDate={isDateRangeDateDisabled}
                            allowClear={false}
                            format="YYYY-MM-DD"
                          />
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Scorecards */}
          <div className="row" data-tour="answer-engine.kpis">
            <div className="col-lg-3 col-md-6 mb-4">
              <ScoreCard
                title={t("answerEngine.totalQuestions", "Total Questions")}
                value={analyticsOverview?.total_questions}
                icon="bi bi-question-circle"
                change={analyticsOverview?.total_question_change}
                isLoaded={analyticsOverviewIsLoaded}
                error={analyticsOverviewError}
                tooltipText={
                  "Total number of questions asked by visitors across all conversations"
                }
              />
            </div>
            <div className="col-lg-3 col-md-6 mb-4">
              <ScoreCard
                title={t("answerEngine.answersProvided", "Answers Provided")}
                value={analyticsOverview?.total_answer_provided}
                icon="bi bi-check-circle"
                change={analyticsOverview?.total_answer_provided_change}
                isLoaded={analyticsOverviewIsLoaded}
                error={analyticsOverviewError}
                tooltipText={
                  "Total number of questions answered by the answer engine across all conversations"
                }
              />
            </div>
            <div className="col-lg-3 col-md-6 mb-4">
              <ScoreCard
                title={t("answerEngine.failedAnswers", "Failed Answers")}
                value={analyticsOverview?.total_failed_answers}
                icon="bi bi-x-circle"
                change={analyticsOverview?.total_failed_answers_change}
                isLoaded={analyticsOverviewIsLoaded}
                error={analyticsOverviewError}
                tooltipText={
                  "Total number of questions unanswered by the answer engine across all conversations"
                }
              />
            </div>
            <div className="col-lg-3 col-md-6 mb-4">
              <ScoreCard
                title={t("answerEngine.conversionRate", "Conversion Rate")}
                value={
                  analyticsOverview?.conversion_rate != null
                    ? `${analyticsOverview.conversion_rate}%`
                    : undefined
                }
                icon="bi bi-graph-up-arrow"
                change={analyticsOverview?.conversion_rate_change}
                isLoaded={analyticsOverviewIsLoaded}
                error={analyticsOverviewError}
                tooltipText={
                  "Percentage of conversations where a visitor clicked your call-to-action"
                }
              />
            </div>
          </div>

          <div className="row" data-tour="answer-engine.engagement">
            <div className="col-lg-3 col-md-6 mb-4">
              <ScoreCard
                title={t("answerEngine.avgSessionDuration", "Avg Session Duration")}
                value={
                  analyticsOverview?.avg_session_duration_minutes != null
                    ? `${analyticsOverview.avg_session_duration_minutes} min`
                    : undefined
                }
                icon="bi bi-stopwatch"
                change={analyticsOverview?.avg_session_time_change}
                isLoaded={analyticsOverviewIsLoaded}
                error={analyticsOverviewError}
                tooltipText={
                  "Average duration of a visitor's conversation with the Answer Engine"
                }
              />
            </div>
            <div className="col-lg-3 col-md-6 mb-4">
              <ScoreCard
                title={t("answerEngine.pageViews", "Page Views")}
                value={analyticsOverview?.page_views}
                icon="bi bi-eye"
                change={analyticsOverview?.page_views_change}
                isLoaded={analyticsOverviewIsLoaded}
                error={analyticsOverviewError}
              />
            </div>
            <div className="col-lg-3 col-md-6 mb-4">
              <ScoreCard
                title={t("answerEngine.uniqueVisitors", "Unique Visitors")}
                value={analyticsOverview?.unique_visitors}
                icon="bi bi-people"
                change={analyticsOverview?.unique_visitors_change}
                isLoaded={analyticsOverviewIsLoaded}
                error={analyticsOverviewError}
                tooltipText={
                  "Number of individual visitors who interacted with the Answer Engine"
                }
              />
            </div>
            <div className="col-lg-3 col-md-6 mb-4">
              <ScoreCard
                title={t("answerEngine.userEngagement", "User Engagement")}
                value={
                  analyticsOverview?.engagement_rate != null
                    ? `${analyticsOverview.engagement_rate}`
                    : undefined
                }
                icon="bi bi-heart-pulse"
                change={analyticsOverview?.engagement_rate_change}
                isLoaded={analyticsOverviewIsLoaded}
                error={analyticsOverviewError}
                tooltipText={
                  "Average number of questions asked per conversation — higher values indicate deeper engagement"
                }
              />
            </div>
          </div>

          <div className="row" data-tour="answer-engine.trends">
            <div className="col-lg-6 col-md-12 mb-4">
              <DonutChart
                title={t("answerEngine.funnelStageDistribution", "Funnel Stage Distribution")}
                data={funnelStageDistribution}
                isLoaded={funnelStageDistributionIsLoaded}
                error={funnelStageDistributionError}
                keyColumn={"funnel_stage"}
                valueColumn={"count"}
                height={250}
              />
            </div>
            <div className="col-lg-6 col-md-12 mb-4">
              <TimeseriesChart
                title={t("answerEngine.conversationCountOverTime", "Conversation Count Over Time")}
                data={chatSessionCountTimeseries ?? []}
                isLoaded={chatSessionCountTimeseriesIsLoaded}
                error={chatSessionCountTimeseriesError}
                xKey="chat_session_date"
                series={[
                  {
                    yKey: "chat_session_count",
                    label: t("answerEngine.conversationCount", "Conversation Count"),
                  },
                  {
                    yKey: "cta_count",
                    label: t("answerEngine.ctaCount", "CTA Count"),
                  },
                ]}
                height={250}
              />
            </div>
          </div>
          <br />
          <div className="row">
            <div className="col-12 mb-4" data-tour="answer-engine.failed-answers">
              <h2 className="h5 fw-bold text-dark mb-3">
                {t("answerEngine.failedAnswers", "Failed Answers")}{" "}
                {failedAnswersIsLoaded ? `(${failedAnswers?.length})` : ""}
              </h2>
              <p className="mt-1 fs-6 text-secondary">
                {t(
                  "answerEngine.failedAnswersSubtitle",
                  "These questions couldn't be answered properly. Review them to improve your content coverage.",
                )}
              </p>
              <SingleColumnTable
                data={enrichedFailedAnswers}
                isLoaded={failedAnswersIsLoaded}
                error={failedAnswersError}
                keyColumn={"detailed_answer"}
                noDataMessage={t(
                  "answerEngine.noFailedAnswers",
                  "No failed answers in this checkpoint period",
                )}
              />
            </div>
          </div>
          <br />
          <div className="row align-items-stretch" data-tour="answer-engine.topics">
            <div className="col-md-6 d-flex flex-column">
              <TwoColumnTable
                title={t("answerEngine.topTopics", "Top Topics")}
                data={queryTopicDistribution ?? []}
                isLoaded={queryTopicDistributionIsLoaded}
                error={queryTopicDistributionError}
                column1Key={"query_topic_level_0"}
                column1Title={t("answerEngine.topicName", "Topic Name")}
                column2Key={"count"}
                column2Title={t("common.count", "Count")}
                columnRatio={"80-20"}
                noDataMessage={t(
                  "answerEngine.noQueryTopics",
                  "No query topics in this checkpoint period",
                )}
              />
            </div>
            <div className="col-md-6 d-flex flex-column">
              <SingleColumnTable
                title={t(
                  "answerEngine.recentHighValueQuestions",
                  "Recent High Value Questions",
                )}
                data={enrichedHighValueQuestions}
                isLoaded={highValueQuestionsIsLoaded}
                error={highValueQuestionsError}
                keyColumn={"detailed_question"}
                exportData={(highValueQuestions ?? []).map((item) => ({
                  detailed_question: [
                    item.query,
                    item.funnel_stage,
                    item.is_branded === true
                      ? "Branded"
                      : item.is_branded === false
                        ? "Non-Branded"
                        : "",
                  ]
                    .filter(Boolean)
                    .join(" | "),
                }))}
                noDataMessage={t(
                  "answerEngine.noHighValueQuestions",
                  "No high value questions in this checkpoint period",
                )}
                containerStyle={{ height: "100%" }}
              />
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
